/**
 * Custom Hook für E-Mail-Erstellung
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useI18n } from './useI18n';
import { useFirebaseFunctions } from './useFirebaseFunctions';
import { anonymizeText } from '../utils/anonymize';
import { getLanguageLabel, getSwissHint } from '../utils/constants';
import { copyTextToClipboard } from '../utils/clipboard';

const AS_MAIL_KEY = 'emailCreation_asMail';
const FORMAL_KEY = 'emailCreation_formal';

export function useEmailCreation() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { translateEmail, isAvailable: functionsAvailable } = useFirebaseFunctions();

  const [inputText, setInputText] = useState('');
  const [resultText, setResultText] = useState('');
  const [targetLang, setTargetLang] = useState('de');
  const [formal, setFormal] = useState(() => {
    const stored = localStorage.getItem(FORMAL_KEY);
    return stored === null ? false : stored === 'true';
  });
  /** true = vollständige Mail mit Anrede/Gruss; false = nur professioneller Fliesstext */
  const [asMail, setAsMail] = useState(() => {
    const stored = localStorage.getItem(AS_MAIL_KEY);
    return stored === null ? true : stored === 'true';
  });
  const [isCreating, setIsCreating] = useState(false);
  const [anonymized, setAnonymized] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    localStorage.setItem(FORMAL_KEY, String(formal));
  }, [formal]);

  useEffect(() => {
    localStorage.setItem(AS_MAIL_KEY, String(asMail));
  }, [asMail]);

  const createEmail = useCallback(async () => {
    if (!inputText.trim()) {
      setError(t.creation.errorEmpty);
      return;
    }

    if (!functionsAvailable) {
      setError(t.common.errorFirebaseUnavailable);
      return;
    }

    if (!user) {
      setError(t.common.errorNotLoggedIn);
      return;
    }

    setIsCreating(true);
    setError('');
    setResultText('');

    try {
      const { text: anonymizedText, replaced } = anonymizeText(inputText);
      setAnonymized(replaced);

      const langLabel = getLanguageLabel(targetLang);
      const schweizHinweis = getSwissHint(targetLang);
      const anrede = formal
        ? 'Verwende die Höflichkeitsform (Sie).'
        : 'Verwende die Du-Form.';

      let prompt;
      if (asMail) {
        prompt = `Hier ist ein grober Text. Erstelle daraus einen professionellen, höflichen E-Mail-Text in ${langLabel}${schweizHinweis}, unabhängig von der Sprache des Ausgangstextes. ${anrede} Füge eine Grussformel ein. Füge bei der Grussformel keine Platzhalter für Namen ein. Füge keine Betreffzeile ein. Gib nur den umformulierten Text zurück.\n\n${anonymizedText}`;
      } else {
        prompt = `Hier ist ein grober Text. Formuliere ihn professionell und klar in ${langLabel}${schweizHinweis}, unabhängig von der Sprache des Ausgangstextes. ${anrede} Schreibe KEINEN E-Mail-Text: keine Anrede, keine Grussformel, keine Betreffzeile, keine Signatur. Gib nur den umformulierten Fliesstext zurück.\n\n${anonymizedText}`;
      }

      const result = await translateEmail(
        {
          model: 'gpt-5.6-sol',
          messages: [
            {
              role: 'system',
              content: asMail
                ? 'Du bist ein professioneller E-Mail-Formulierungsassistent.'
                : 'Du bist ein professioneller Textredakteur. Du schreibst klare, professionelle Texte ohne E-Mail-Anrede oder Grussformel.',
            },
            { role: 'user', content: prompt },
          ],
          maxTokens: 2500,
          temperature: 0.3,
        },
        user
      );

      if (result.data && result.data.content) {
        setResultText(result.data.content);
      } else {
        setError(t.creation.errorNoResponse);
      }
    } catch (err) {
      setError(t.creation.errorGeneric.replace('{error}', err.message || ''));
    } finally {
      setIsCreating(false);
    }
  }, [
    inputText,
    targetLang,
    formal,
    asMail,
    user,
    functionsAvailable,
    translateEmail,
    t,
  ]);

  const copyToClipboard = useCallback(async () => {
    if (!resultText) return false;
    return copyTextToClipboard(resultText);
  }, [resultText]);

  const reset = useCallback(() => {
    setInputText('');
    setResultText('');
    setError('');
    setAnonymized(false);
  }, []);

  return {
    inputText,
    setInputText,
    resultText,
    targetLang,
    setTargetLang,
    formal,
    setFormal,
    asMail,
    setAsMail,
    isCreating,
    anonymized,
    error,
    createEmail,
    copyToClipboard,
    reset,
  };
}
